import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Languages } from "lucide-react";
import { scrollToSection } from "@/components/Navbar";
import { useLang } from "@/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1609810074355-29137827b2c4?fm=jpg&q=85&w=2400&auto=format&fit=crop";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.16, duration: 1.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { t, lang } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const lines = [
    { text: t.hero.line1, cls: "text-bone" },
    { text: t.hero.line2, cls: "text-bone" },
    { text: t.hero.line3, cls: "italic text-copper" },
  ];

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform">
        <img
          src={HERO_IMG}
          alt={t.hero.alt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 h-full max-w-[1600px] mx-auto px-6 sm:px-10 flex flex-col justify-end pb-20 sm:pb-24"
      >
        <motion.p
          data-testid="hero-overline"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8, ease: "easeOut" }}
          className="text-xs tracking-[0.35em] uppercase text-copper mb-8"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-display tracking-tight leading-[0.95] text-[13.5vw] sm:text-[10vw] lg:text-[8vw]">
          {lines.map((l, i) => (
            <span key={`${lang}-${l.text}`} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${l.cls}`}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end gap-8 sm:gap-16"
        >
          <div className="max-w-md">
            <p data-testid="hero-subtext" className="text-sm sm:text-base leading-relaxed text-bone/60">
              {t.hero.subtext}
            </p>
            <p
              data-testid="hero-espanol-chip"
              className="mt-5 inline-flex items-center gap-2 border border-copper/50 text-copper text-[10px] tracking-[0.3em] uppercase px-4 py-2"
            >
              <Languages size={13} />
              {t.hero.espanol}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="hero-cta-button"
              onClick={() => scrollToSection("#contact")}
              className="btn-swipe group flex items-center gap-3 border border-copper text-copper px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-300"
            >
              {t.hero.cta}
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-scroll-button"
              onClick={() => scrollToSection("#about")}
              aria-label={t.hero.scrollLabel}
              className="w-12 h-12 border border-white/20 flex items-center justify-center text-bone/60 hover:text-copper hover:border-copper transition-colors duration-300"
            >
              <motion.span animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
                <ArrowDown size={16} />
              </motion.span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
